import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, XCircle, Clock, TrendingDown } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import { Container, SectionHead, Button } from "../components/site/ui";
import { Reveal } from "../components/site/Reveal";
import PackageCards from "../components/site/PackageCards";
import HeroCarousel from "../components/site/HeroCarousel";
import { INDUSTRIES, PROCESS, WHY, CONCEPTS } from "../data/site";

const PROBLEMS = [
  { icon: XCircle, title: "No Website", desc: "Customers can't find you — and they trust businesses they can look up online." },
  { icon: Clock, title: "Outdated Website", desc: "A dated site quietly costs you credibility and customers every single day." },
  { icon: TrendingDown, title: "Doesn't Generate Customers", desc: "A pretty site isn't enough. Yours should book jobs, sell, and capture leads." },
];

export default function Home() {
  return (
    <SiteShell title="GoBuildWeb — Build. Launch. Grow. | Websites starting at $999" description="Professional websites and digital systems built to help your business get found, get customers, and grow. Websites starting at $999.">
      {/* HERO CAROUSEL */}
      <HeroCarousel />

      {/* PROBLEM → SOLUTION */}
      <section className="py-16 md:py-24">
        <Container>
          <SectionHead center eyebrow="The problem" title="Most businesses lose customers online" />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                  <p.icon size={26} className="text-gold-500" />
                  <h3 className="mt-4 font-display text-xl font-bold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl border border-gold-500/30 bg-gradient-to-r from-gold-500/[0.08] to-transparent p-8 text-center md:p-10">
              <p className="mx-auto max-w-2xl font-display text-2xl font-bold text-white md:text-3xl">
                GoBuildWeb builds the digital foundation your business can grow on.
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* 3 PACKAGES — the centerpiece */}
      <section className="py-16 md:py-24">
        <Container>
          <SectionHead center eyebrow="Start here" title="Three ways to start — starting at $999" sub="Begin with Basic to get online, then grow into a complete digital system on your timeline." />
          <div className="mt-14"><PackageCards /></div>
          <div className="mt-8 text-center"><Button to="/pricing" variant="ghost">Compare packages</Button></div>
        </Container>
      </section>

      {/* WHAT WE CAN BUILD */}
      <section className="py-16 md:py-24">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHead eyebrow="What we can build" title="See what your business could look like" sub="Polished concept sites that show the quality we deliver." />
            <Button to="/what-we-can-build" variant="ghost">View all</Button>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {CONCEPTS.slice(0, 3).map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link to={`/what-we-can-build/${c.slug}`} className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]" data-testid={`home-concept-${c.slug}`}>
                  <div className="relative aspect-[16/11] overflow-hidden">
                    <img src={c.img} alt={c.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                    <span className="absolute left-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gold-400 ring-1 ring-gold-500/40">{c.tag}</span>
                  </div>
                  <div className="p-5">
                    <div className="text-xs text-gold-500">{c.industry}</div>
                    <h3 className="mt-1 font-display text-lg font-bold text-white">{c.name}</h3>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-white group-hover:text-gold-400">View concept <ArrowRight size={14} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 md:py-24">
        <Container>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionHead eyebrow="Who it's for" title="Built for your industry" />
            <Button to="/industries" variant="ghost">All industries</Button>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {INDUSTRIES.slice(0, 6).map((ind, i) => (
              <Reveal key={ind.id} delay={i * 0.05}>
                <Link to="/industries" className="group block overflow-hidden rounded-2xl border border-white/10" data-testid={`home-industry-${ind.id}`}>
                  <div className="relative aspect-square overflow-hidden">
                    <img src={ind.img} alt={ind.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-3.5">
                      <ind.icon size={16} className="text-gold-400" />
                      <div className="mt-1 text-sm font-semibold text-white">{ind.name}</div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-24">
        <Container>
          <SectionHead center eyebrow="How it works" title="From idea to launch in six steps" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                  <span className="font-display text-4xl font-bold text-white/10">0{s.n}</span>
                  <h3 className="mt-3 font-display text-lg font-bold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY */}
      <section className="py-16 md:py-24">
        <Container>
          <SectionHead center eyebrow="Why GoBuildWeb" title="A technology partner, not a template" />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.06}>
                <div className="flex h-full items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-gold-500/10 text-gold-400"><w.icon size={18} /></span>
                  <div>
                    <h3 className="font-semibold text-white">{w.title}</h3>
                    <p className="mt-1 text-sm text-zinc-400">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <FinalCTA />
    </SiteShell>
  );
}

export const FinalCTA = () => (
  <section className="py-16 md:py-24">
    <Container>
      <div className="relative overflow-hidden rounded-3xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.12] to-transparent p-10 text-center md:p-16">
        <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-gold-500/10 blur-3xl" />
        <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-white md:text-5xl">Ready to build your business online?</h2>
        <p className="mx-auto mt-4 max-w-xl text-zinc-300">Let's build something your business can grow with — starting at $999.</p>
        <div className="mt-8 flex justify-center">
          <Button to="/get-started" size="lg" data-testid="final-get-website">Get My Website</Button>
        </div>
      </div>
    </Container>
  </section>
);
